// Reports (contracts/phase2.md §8 safety, §12): the moderation queue. Editors
// and admins dismiss a report or action it (reset the handle, or hold the
// player off the global leaderboards) after a confirm step; the API audits it.

import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useReports, useUpdateReport } from "../api/queries";
import type { Report, ReportPatch } from "../api/schemas";
import { useAuth, useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, Chip, ConfirmDialog, EmptyState, ErrorState, PageHeader, Segmented, Skeleton } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import { shortId } from "../lib/format";
import styles from "./pages.module.css";
import { REASON_LABEL } from "./PlayerPhase2";

type Pending = { report: Report; kind: "dismiss" | "reset_handle" | "hide_from_leaderboards" };

const ACTION: Record<Pending["kind"], { title: string; confirm: string; patch: ReportPatch; tone: "primary" | "danger" }> = {
  dismiss: { title: "Dismiss this report?", confirm: "Dismiss", patch: { status: "dismissed", action: "none" }, tone: "primary" },
  reset_handle: { title: "Reset the reported handle?", confirm: "Reset handle", patch: { status: "actioned", action: "reset_handle" }, tone: "danger" },
  hide_from_leaderboards: {
    title: "Hide this player from leaderboards?",
    confirm: "Hide from leaderboards",
    patch: { status: "actioned", action: "hide_from_leaderboards" },
    tone: "danger",
  },
};

const ACTION_LABEL: Record<string, string> = { none: "dismissed", reset_handle: "handle reset", hide_from_leaderboards: "hidden from boards" };

export function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") === "closed" ? "closed" : "open";
  const q = useReports(status);
  const canAct = useCan("reports.resolve");
  const { admin } = useAuth();
  const [pending, setPending] = useState<Pending | null>(null);
  const mutation = useUpdateReport();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  const ask = (report: Report, kind: Pending["kind"]) => {
    mutation.reset();
    setPending({ report, kind });
  };

  return (
    <>
      <PageHeader
        title="Reports"
        meta={
          canAct ? (
            <span>Player reports (handle, cheating, other). Each decision is final and written to the audit log.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · resolving reports needs the editor role (you are {admin?.role})
            </span>
          )
        }
      />
      <div className={styles.filters}>
        <Segmented
          label="Status"
          value={status}
          onChange={(v) => setParams(v === "open" ? {} : { status: v }, { replace: true })}
          options={[
            { value: "open", label: "Open" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <span className={styles.filtersEnd}>
          <span className="monoLabel">{q.data ? `${items.length}${q.hasNextPage ? "+" : ""} ${status}` : ""}</span>
        </span>
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className={tableStyles.wrap} aria-busy={q.isFetching}>
          <table className={tableStyles.table} data-testid="reports">
            <thead>
              <tr>
                <th>Reported</th>
                <th>Reason</th>
                <th>Player</th>
                <th>Note</th>
                <th>Reporter</th>
                <th>{status === "open" ? (canAct ? "Actions" : "") : "Outcome"}</th>
              </tr>
            </thead>
            <tbody>
              {q.isPending
                ? Array.from({ length: 6 }, (_, i) => (
                    <tr key={i}>
                      {[90, 60, 200, 180, 80, 160].map((w, j) => (
                        <td key={j}>
                          <Skeleton height={12} width={w} />
                        </td>
                      ))}
                    </tr>
                  ))
                : items.map((r) => (
                    <tr key={r.id} data-report={r.id}>
                      <td className={tableStyles.mono}>{formatDateTime(r.createdAt)}</td>
                      <td>
                        <Chip tone={r.reason === "cheating" ? "danger" : r.reason === "handle" ? "warning" : "neutral"}>{REASON_LABEL[r.reason] ?? r.reason}</Chip>
                      </td>
                      <td>
                        <div className={styles.reportTarget}>
                          <span className={styles.reportWho}>
                            {r.reported.handle ? (
                              <span className="mono">@{r.reported.handle}</span>
                            ) : r.reported.handleAtReport ? (
                              <span className={`mono ${styles.faint}`} title="The handle has since been reset">
                                <s>@{r.reported.handleAtReport}</s>
                              </span>
                            ) : (
                              <span className={styles.faint}>{r.reported.name ?? "no handle"}</span>
                            )}
                            {r.reported.reports > 1 ? <Chip tone="warning">{`${r.reported.reports} reports`}</Chip> : null}
                            {r.reported.moderationHold ? <Chip tone="muted">board hold</Chip> : null}
                          </span>
                          <Link to={`/players/${r.reported.userId}`} className={tableStyles.id}>
                            {shortId(r.reported.userId, 13)}
                          </Link>
                        </div>
                      </td>
                      <td className={styles.reportNoteCell}>{r.note ?? <span className={styles.faint}>—</span>}</td>
                      <td>
                        {r.reporter ? (
                          <Link to={`/players/${r.reporter.userId}`} className={`${tableStyles.id} ${tableStyles.dim}`}>
                            {r.reporter.handle ? `@${r.reporter.handle}` : shortId(r.reporter.userId, 8)}
                          </Link>
                        ) : (
                          <span className={styles.faint}>deleted</span>
                        )}
                      </td>
                      <td>
                        {r.status === "open" ? (
                          canAct ? (
                            <div className={styles.rowActions}>
                              <Button size="sm" variant="ghost" onClick={() => ask(r, "dismiss")}>
                                Dismiss
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => ask(r, "reset_handle")}
                                disabled={!r.reported.handle}
                                title={r.reported.handle ? undefined : "This player has no handle"}
                              >
                                Reset handle
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => ask(r, "hide_from_leaderboards")}
                                disabled={r.reported.moderationHold}
                                title={r.reported.moderationHold ? "Already held off the leaderboards" : undefined}
                              >
                                Hide from boards
                              </Button>
                            </div>
                          ) : null
                        ) : (
                          <div className={styles.reportOutcome}>
                            <Chip tone={r.status === "actioned" ? "accent" : "muted"}>{ACTION_LABEL[r.action ?? "none"] ?? r.action}</Chip>
                            <span className={styles.faint}>
                              {r.resolvedBy ?? "—"}
                              {r.resolvedAt ? ` · ${formatDateTime(r.resolvedAt)}` : ""}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
          {!q.isPending && items.length === 0 ? (
            <EmptyState title={status === "open" ? "The queue is empty" : "No closed reports"}>
              {status === "open" ? "Reports players file from the app land here." : "Dismissed and actioned reports show here."}
            </EmptyState>
          ) : null}
          {q.hasNextPage ? (
            <div className={styles.loadMore}>
              <Button size="sm" onClick={() => void q.fetchNextPage()} busy={q.isFetchingNextPage}>
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={pending ? ACTION[pending.kind].title : ""}
        confirmLabel={pending ? ACTION[pending.kind].confirm : ""}
        tone={pending ? ACTION[pending.kind].tone : "primary"}
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setPending(null)}
        onConfirm={() =>
          pending && mutation.mutate({ id: pending.report.id, patch: ACTION[pending.kind].patch }, { onSuccess: () => setPending(null) })
        }
      >
        {pending ? <ConfirmBody pending={pending} /> : null}
      </ConfirmDialog>
    </>
  );
}

function ConfirmBody({ pending: { report: r, kind } }: { pending: Pending }) {
  const who = r.reported.handle ? `@${r.reported.handle}` : (r.reported.name ?? shortId(r.reported.userId, 13));
  return (
    <div className={styles.prose}>
      <p>
        {kind === "dismiss" ? (
          <>
            The report against <span className="mono">{who}</span> is closed with no action.
          </>
        ) : kind === "reset_handle" ? (
          <>
            <span className="mono">{who}</span> loses the handle; it disappears from profiles, friends lists, leaderboards and share cards.
            They can pick a new one in the app.
          </>
        ) : (
          <>
            <span className="mono">{who}</span> is held off every global leaderboard. Friends still see them.
          </>
        )}
      </p>
      <p className={styles.faint}>
        {REASON_LABEL[r.reason] ?? r.reason} report {r.id}
        {r.note ? `: “${r.note}”` : ""}. Written to the audit log.
      </p>
    </div>
  );
}
